import AvatarCore from "@/features/avatar/core/AvatarCore";
import type { ElevenLabsAlignment } from "@/lib/visemeMapper";
import type { LipSyncMorphTarget } from "@/hooks/useLipSync";

// Override prop accepts EITHER a single number (applied to both providers,
// legacy behavior) OR a per-provider object. This lets call-sites calibrate
// Avaturn without breaking MetaPerson framing (the two providers interpret
// the same numeric override differently — see AvatarCore.tsx:205 vs :237).
//
// Examples:
//   scaleOverride={0.3}                               // both providers get 0.3
//   scaleOverride={{ avaturn: 0.5 }}                  // Avaturn=0.5, MetaPerson untouched
//   scaleOverride={{ avaturn: 0.5, metaperson: 1.2 }} // explicit per provider
export type AvatarOverride = number | { avaturn?: number; metaperson?: number };

interface AvatarViewerProps {
  modelUrl?: string;
  height?: string;
  enableRotate?: boolean;
  framing?: "full" | "bust";
  isAvaturnModel?: boolean;
  analyserNode?: AnalyserNode | null;
  lookAtYOverride?: AvatarOverride;
  scaleOverride?: AvatarOverride;
  cameraDistanceOverride?: AvatarOverride;
  animationUrl?: string;
  // Fase 4 lip sync (opt-in pass-through to AvatarCore).
  audioElement?: HTMLAudioElement | null;
  alignment?: ElevenLabsAlignment | null;
  // LOTE 4.1 (05/09/2026, autorização explícita do Rafa): repasse puro do
  // prop opcional do AvatarCore — o alvo de lip sync para o driver de boca
  // do LIVE (MirrorLiveAvatar). Sem ele, nada muda.
  onLipSyncTargetReady?: (target: LipSyncMorphTarget | null) => void;
}

function resolveOverride(
  override: AvatarOverride | undefined,
  isAvaturn: boolean,
): number | undefined {
  if (override === undefined) return undefined;
  if (typeof override === "number") return override;
  return isAvaturn ? override.avaturn : override.metaperson;
}

export default function AvatarViewer({
  modelUrl = "/avatar/model_small.glb",
  height = "480px",
  enableRotate = true,
  framing = "full",
  isAvaturnModel = false,
  analyserNode,
  lookAtYOverride,
  scaleOverride,
  cameraDistanceOverride,
  animationUrl,
  audioElement,
  alignment,
  onLipSyncTargetReady,
}: AvatarViewerProps) {
  const resolvedLookAtY = resolveOverride(lookAtYOverride, isAvaturnModel);
  const resolvedScale = resolveOverride(scaleOverride, isAvaturnModel);
  const resolvedCameraDistance = resolveOverride(cameraDistanceOverride, isAvaturnModel);

  return (
    <AvatarCore
      modelUrl={modelUrl}
      height={height}
      enableRotate={enableRotate}
      framing={framing}
      isAvaturnModel={isAvaturnModel}
      analyserNode={analyserNode}
      lookAtYOverride={resolvedLookAtY}
      scaleOverride={resolvedScale}
      cameraDistanceOverride={resolvedCameraDistance}
      animationUrl={animationUrl}
      audioElement={audioElement}
      alignment={alignment}
      onLipSyncTargetReady={onLipSyncTargetReady}
    />
  );
}
